import { useState, useEffect, useRef } from "react";
import { Facebook, Instagram, Youtube, Mail, ChevronDown, X, Search } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import flagLogoAsset from "@/assets/rsg-flag-fulllogo.png.asset.json";
import CartButton from "@/components/CartButton";
import SearchDrawer from "@/components/SearchDrawer";
import { Button } from "@/components/ui/button";

const XIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);
  const [gamesDropdownOpen, setGamesDropdownOpen] = useState(false);
  const gamesMenuRef = useRef<HTMLDivElement>(null);
  const gamesToggleRef = useRef<HTMLButtonElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [hasScrolled, setHasScrolled] = useState(() => window.scrollY > 0);
  const { settings } = useSiteSettings();
  const location = useLocation();
  const topbarEnabled = settings.topbar_enabled !== "false";
  const topbarText = settings.topbar_text || "Site Under Construction: game info is being updated";

  useEffect(() => {
    document.documentElement.style.setProperty("--topbar-height", topbarEnabled ? "36px" : "0px");
  }, [topbarEnabled]);

  // Fetch published games for dropdown
  const { data: games = [] } = useQuery({
    queryKey: ["header-games"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("games")
        .select("id, title")
        .eq("is_published", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setGamesDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!gamesDropdownOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!gamesMenuRef.current?.contains(event.target as Node)) setGamesDropdownOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setGamesDropdownOpen(false);
        gamesToggleRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [gamesDropdownOpen]);

  // Move focus into the drawer when it opens, and back to the menu button
  // when it closes (including Escape and link activation).
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      const timer = setTimeout(() => {
        drawerRef.current?.querySelector<HTMLElement>("a[href], button:not([disabled])")?.focus();
      }, 320); // wait for the drawer's open transition to finish
      return () => clearTimeout(timer);
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      menuButtonRef.current?.focus();
    }
  }, [isOpen]);

  const handleDrawerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      setIsOpen(false);
      return;
    }

    const container = drawerRef.current;
    if (!container) return;
    const focusables = Array.from(container.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"));
    if (focusables.length === 0) return;

    const currentIndex = focusables.indexOf(document.activeElement as HTMLElement);

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      let nextIndex: number;
      if (event.key === "ArrowDown") {
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % focusables.length;
      } else {
        nextIndex =
          currentIndex < 0 ? focusables.length - 1 : (currentIndex - 1 + focusables.length) % focusables.length;
      }
      focusables[nextIndex].focus();
      return;
    }

    // Wrap Tab at the drawer's edges so focus can't escape while it's open.
    if (event.key === "Tab") {
      const forward = !event.shiftKey;
      if (forward && currentIndex === focusables.length - 1) {
        event.preventDefault();
        focusables[0].focus();
      } else if (!forward && currentIndex === 0) {
        event.preventDefault();
        focusables[focusables.length - 1].focus();
      }
    }
  };

  useEffect(() => {
    const updateHeaderBackground = () => setHasScrolled(window.scrollY > 0);

    updateHeaderBackground();
    window.addEventListener("scroll", updateHeaderBackground, { passive: true });

    return () => window.removeEventListener("scroll", updateHeaderBackground);
  }, [location.pathname]);

  const transparentHeaderPages = ["/", "/about", "/contact", "/games"];
  // The header must always be solid while the mobile drawer is open, so the
  // drawer's dark background never sits behind a see-through header.
  const headerIsTransparent = transparentHeaderPages.includes(location.pathname) && !hasScrolled && !isOpen;

  const newsEnabled = settings.news_enabled !== "false";

  const navLinks = [
    { name: "Shop", href: "/shop" },
    ...(newsEnabled ? [{ name: "News", href: "/news" }] : []),
    { name: "About", href: "/about" },
    { name: "Contact", href: "/contact" },
  ];

  const allSocialLinks = [
    { icon: Facebook, href: settings.social_facebook, label: "Facebook" },
    { icon: Instagram, href: settings.social_instagram, label: "Instagram" },
    { icon: XIcon, href: settings.social_x, label: "X" },
    { icon: Youtube, href: settings.social_youtube, label: "YouTube" },
    { icon: Mail, href: settings.social_email ? `mailto:${settings.social_email}` : "", label: "Email" },
  ];

  const socialLinks = allSocialLinks.filter((link) => link.href && link.href !== "" && link.href !== "mailto:");

  return (
    <>
      {/* Site status bar - always visible, above the header on every page */}
      {topbarEnabled && (
        <div className="fixed top-0 left-0 right-0 z-[55] bg-topbar border-b border-topbar-foreground/10">
          <div className="flex h-9 items-center justify-center px-4">
            <p className="text-center text-[11px] tracking-[0.08em] uppercase text-topbar-foreground/80 sm:text-xs">
              {topbarText}
            </p>
          </div>
        </div>
      )}

      {/* Header Bar - Always on top */}
      <header
        className={`fixed top-[var(--topbar-height)] left-0 right-0 z-50 text-header-foreground transition-colors duration-500 ${
          headerIsTransparent ? "bg-transparent" : "bg-header"
        }`}
      >
        <div className="px-8">
          <div className="relative flex items-center justify-between h-20">
            {/* Hanging flag logo */}
            <Link
              to="/"
              className="absolute left-0 top-0 z-[60] block w-[90px]"
              onClick={() => setIsOpen(false)}
              aria-label="Rogue Skull Games home"
            >
              <img
                src={settings.site_header_logo || flagLogoAsset.url}
                alt="Rogue Skull Games"
                className="block w-full h-auto"
                width={1359}
                height={1920}
                fetchPriority="high"
              />
            </Link>

            <div className="w-[83px] sm:w-[96px] md:w-[129px] lg:w-[144px] shrink-0" aria-hidden="true" />

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-2 lg:gap-8 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              {/* <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href="https://www.thegamecrafter.com/designers/rogue-skull-games"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative text-sm text-dark-foreground hover:text-dark-foreground transition-colors duration-300 uppercase tracking-[0.15em] font-medium group flex items-center gap-1"
                    >
                      Shop
                      <ExternalLink className="w-3 h-3" />
                      <span className="absolute -bottom-1 left-0 w-0 h-px bg-blood-red transition-all duration-300 group-hover:w-full" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Opens TheGameCrafter store</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider> */}

              {/* Games with dropdown */}
              <div
                ref={gamesMenuRef}
                className="relative"
                onPointerEnter={(event) => {
                  if (event.pointerType === "mouse") setGamesDropdownOpen(true);
                }}
                onPointerLeave={(event) => {
                  if (event.pointerType === "mouse") setGamesDropdownOpen(false);
                }}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node)) setGamesDropdownOpen(false);
                }}
              >
                <div className="flex items-center">
                  <Link
                    to="/games"
                    className="relative min-h-11 flex items-center text-sm text-header-foreground hover:text-blood-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors duration-300 uppercase tracking-[0.12em] font-bold group pl-3 pr-1 lg:pl-4"
                  >
                    Games
                    <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-blood-red transition-all duration-300 group-hover:w-full" />
                  </Link>
                  <Button
                    ref={gamesToggleRef}
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Toggle Games menu"
                    aria-expanded={gamesDropdownOpen}
                    aria-controls="header-games-menu"
                    onClick={() => setGamesDropdownOpen((open) => !open)}
                    className="h-11 w-11 shrink-0 text-header-foreground hover:bg-transparent hover:text-blood-red focus-visible:ring-ring [&_svg]:!size-5"
                  >
                    <ChevronDown
                      className={`transition-transform duration-200 ${gamesDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </Button>
                </div>

                {/* Dropdown */}
                <div
                  id="header-games-menu"
                  className={`absolute top-full left-0 pt-2 min-w-[180px] transition-all duration-200 ${
                    gamesDropdownOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
                  }`}
                >
                  <div className="py-2 bg-header border border-header-foreground/15 rounded-lg shadow-2xl">
                    {games.map((game) => {
                      const slug = game.title
                        .toLowerCase()
                        .replace(/\s+/g, "-")
                        .replace(/[^a-z0-9-]/g, "");
                      return (
                        <Link
                          key={game.id}
                          to={`/games/${slug}`}
                          className="flex items-center min-h-11 px-4 py-2 text-header-foreground hover:text-blood-red hover:bg-header-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors duration-200"
                        >
                          {game.title}
                        </Link>
                      );
                    })}
                    {games.length === 0 && (
                      <span className="block px-4 py-2 text-sm text-header-foreground/70">No games yet</span>
                    )}
                  </div>
                </div>
              </div>

              {newsEnabled && (
                <Link
                  to="/news"
                  className="relative min-h-11 flex items-center text-sm text-header-foreground hover:text-blood-red transition-colors duration-300 uppercase tracking-[0.12em] font-bold group px-3 lg:px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  News
                  <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-blood-red transition-all duration-300 group-hover:w-full" />
                </Link>
              )}

              <Link
                to="/about"
                className="relative min-h-11 flex items-center text-sm text-header-foreground hover:text-blood-red transition-colors duration-300 uppercase tracking-[0.12em] font-bold group px-3 lg:px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                About
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-blood-red transition-all duration-300 group-hover:w-full" />
              </Link>

              <Link
                to="/contact"
                className="relative min-h-11 flex items-center text-sm text-header-foreground hover:text-blood-red transition-colors duration-300 uppercase tracking-[0.12em] font-bold group px-3 lg:px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Contact
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-blood-red transition-all duration-300 group-hover:w-full" />
              </Link>
            </nav>

            {/* Desktop actions - right */}
            <div className="hidden md:flex items-center gap-6">
              <Button
                variant="ghost"
                size="icon"
                onClick={(event) => { searchTriggerRef.current = event.currentTarget; setSearchOpen(true); }}
                aria-label="Search the site"
                className="min-h-11 min-w-11 text-header-foreground hover:bg-transparent hover:text-blood-red transition-colors duration-300"
              >
                <Search className="w-5 h-5" />
              </Button>

              <CartButton className="text-header-foreground" />
            </div>

            {/* Mobile actions */}
            <div className="md:hidden flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={(event) => { searchTriggerRef.current = event.currentTarget; setSearchOpen(true); }}
                aria-label="Search the site"
                className="min-h-11 min-w-11 text-header-foreground hover:bg-transparent hover:text-blood-red transition-colors duration-300"
              >
                <Search className="w-5 h-5" />
              </Button>

              <CartButton className="text-header-foreground" />

              {/* Mobile Menu Button */}
              <button
                ref={menuButtonRef}
                className="p-2 text-header-foreground hover:text-blood-red transition-colors duration-300"
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={isOpen}
                aria-controls="mobile-navigation"
              >
                {isOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <div className="relative w-6 h-5">
                    <span className="absolute left-0 top-0 w-full h-0.5 bg-current" />
                    <span className="absolute left-0 top-2 w-full h-0.5 bg-current" />
                    <span className="absolute left-0 top-4 w-full h-0.5 bg-current" />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Below header */}
      <div
        id="mobile-navigation"
        ref={drawerRef}
        onKeyDown={handleDrawerKeyDown}
        className={`md:hidden fixed top-[calc(var(--topbar-height)_+_5rem)] left-0 right-0 bottom-0 bg-header text-header-foreground z-40 transition-all duration-300 ${
          isOpen ? "opacity-100 visible pointer-events-auto" : "opacity-0 invisible pointer-events-none"
        }`}
      >
        <div className="px-8 pb-8 pt-16 h-full flex flex-col overflow-y-auto">
          <nav className="flex flex-col gap-1">
            {/* <a
              href="https://www.thegamecrafter.com/designers/rogue-skull-games"
              target="_blank"
              rel="noopener noreferrer"
              className={`text-3xl font-display font-bold text-dark-foreground py-3 transition-all duration-300 flex items-center gap-2 ${
                isOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              }`}
              style={{ transitionDelay: "0ms" }}
              onClick={() => setIsOpen(false)}
            >
              Shop
              <ExternalLink className="w-5 h-5" />
            </a> */}
            <Link
              to="/games"
              className={`text-2xl font-display font-bold text-header-foreground hover:text-blood-red py-2 transition-all duration-300 ${
                isOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              }`}
              style={{ transitionDelay: "50ms" }}
              onClick={() => setIsOpen(false)}
            >
              Games
            </Link>

            {games.length > 0 && (
              <div className="flex flex-col border-l border-header-foreground/20 ml-1 mb-2">
                {games.map((game, index) => {
                  const slug = game.title
                    .toLowerCase()
                    .replace(/\s+/g, "-")
                    .replace(/[^a-z0-9-]/g, "");

                  return (
                    <Link
                      key={game.id}
                      to={`/games/${slug}`}
                      className={`text-base font-medium text-header-foreground/75 hover:text-blood-red px-4 py-1.5 transition-all duration-300 ${
                        isOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
                      }`}
                      style={{ transitionDelay: `${(index + 2) * 40}ms` }}
                      onClick={() => setIsOpen(false)}
                    >
                      {game.title}
                    </Link>
                  );
                })}
              </div>
            )}

            {navLinks
              .filter((link) => link.name !== "Shop")
              .map((link, index) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-2xl font-display font-bold text-header-foreground hover:text-blood-red py-2 transition-all duration-300 ${
                    isOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
                  }`}
                  style={{ transitionDelay: `${(games.length + index + 2) * 40}ms` }}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
          </nav>

          <div
            className={`mt-auto pt-8 border-t border-border/40 transition-all duration-300 ${
              isOpen ? "opacity-100" : "opacity-0"
            }`}
            style={{ transitionDelay: "200ms" }}
          >
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={`Follow us on ${social.label}`}
                  className="w-12 h-12 rounded-full border border-header-foreground/25 flex items-center justify-center text-header-foreground/70 hover:text-blood-red hover:border-blood-red transition-all duration-300"
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <SearchDrawer open={searchOpen} onOpenChange={setSearchOpen} returnFocusRef={searchTriggerRef} />
    </>
  );
};

export default Header;
